import { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { identifyUser, markTestAccount, resetIdentity } from "@/lib/posthog";
import { isTestAccountEmail } from "@/lib/qaAccounts";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
  ) => Promise<{ error: Error | null; needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: Error | null }>;
  reauthenticate: (password: string) => Promise<{ error: Error | null }>;
  updateEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const welcomeSentRef = useRef<Set<string>>(new Set());
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // CRITICAL: never await inside onAuthStateChange — Supabase docs warn this
    // can deadlock the listener and cause "stuck loading" / silent session
    // failures, especially on mobile Safari after backgrounding.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (cancelled) return;

      // Only update state when the user identity actually changes.
      // Supabase emits new session objects on TOKEN_REFRESHED — re-rendering
      // every consumer on each refresh causes downstream effects (useHousehold,
      // route guards) to re-run and can flash the paywall.
      const nextUserId = nextSession?.user?.id ?? null;
      const userIdChanged = nextUserId !== lastUserIdRef.current;

      setSession(nextSession);
      if (userIdChanged) {
        lastUserIdRef.current = nextUserId;
        setUser(nextSession?.user ?? null);
      } else if (nextSession?.user && !user) {
        // Edge case: user was null but session restored
        setUser(nextSession.user);
      }
      setLoading(false);

      // SIGNED_OUT — clear any stale local data so the next login starts clean.
      if (event === "SIGNED_OUT") {
        welcomeSentRef.current.clear();
      }

      // Welcome email — fire-and-forget, deferred to next tick to avoid
      // blocking the auth state machine (see Supabase docs).
      if (event === "SIGNED_IN" && nextSession?.user) {
        const uid = nextSession.user.id;
        const email = nextSession.user.email;
        const accessToken = nextSession.access_token;
        if (email && accessToken && !welcomeSentRef.current.has(uid)) {
          welcomeSentRef.current.add(uid);
          setTimeout(() => {
            void (async () => {
              try {
                const { count } = await supabase
                  .from("email_send_log")
                  .select("id", { count: "exact", head: true })
                  .eq("recipient_email", email)
                  .eq("template_name", "welcome-email")
                  .eq("status", "sent");
                if ((count ?? 0) === 0) {
                  await supabase.functions.invoke("send-transactional-email", {
                    headers: { Authorization: `Bearer ${accessToken}` },
                    body: {
                      templateName: "welcome-email",
                      recipientEmail: email,
                      idempotencyKey: `welcome-${uid}`,
                      templateData: {
                        name: nextSession.user.user_metadata?.full_name || undefined,
                      },
                    },
                  });
                }
              } catch {
                // Non-critical
              }
            })();
          }, 0);
        }
      }
    });

    // Initial session restore. If this fails (invalid refresh token, network),
    // we MUST still resolve loading=false so the app doesn't hang forever.
    supabase.auth
      .getSession()
      .then(({ data: { session: initial }, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("[useAuth] getSession error:", error.message);
        }
        const initialUserId = initial?.user?.id ?? null;
        lastUserIdRef.current = initialUserId;
        setSession(initial);
        setUser(initial?.user ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[useAuth] getSession threw:", err);
        setSession(null);
        setUser(null);
        setLoading(false);
      });

    // Safety valve: if neither getSession nor onAuthStateChange resolves
    // within 6s (rare, but observed on iOS Safari with stale tokens),
    // force the spinner off so the user can reach the login screen.
    const safetyTimer = setTimeout(() => {
      if (cancelled) return;
      setLoading((prev) => {
        if (prev) console.warn("[useAuth] Safety valve fired — auth init timed out");
        return false;
      });
    }, 6000);

    // Revalidate session when the tab becomes visible again. Mobile Safari
    // suspends timers/network in background; without this the app keeps
    // showing stale state until the user manually refreshes.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void supabase.auth.getSession().then(async ({ data: { session: s }, error }) => {
        if (error) {
          console.warn("[useAuth] Revalidate failed:", error.message);
          return;
        }
        // If the session disappeared while we were backgrounded (token
        // expired & refresh failed), force a clean signed-out state.
        if (!s && lastUserIdRef.current) {
          // Give Supabase one chance to refresh the token before we nuke
          // local state — mobile Safari sometimes returns null from
          // getSession() while the refresh token is still valid.
          try {
            const { data: refreshed } = await supabase.auth.refreshSession();
            if (refreshed?.session) {
              setSession(refreshed.session);
              setUser(refreshed.session.user);
              return;
            }
          } catch (e) {
            console.warn("[useAuth] refreshSession threw:", e);
          }
          console.warn("[useAuth] Session lost while backgrounded — signing out locally");
          lastUserIdRef.current = null;
          setSession(null);
          setUser(null);
        }
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // PostHog identity — opaque Supabase UUID only. Never email, real name,
  // child name or household name. Identifying (rather than resetting) here
  // preserves anonymous → authenticated attribution at signup/login.
  useEffect(() => {
    if (!user?.id) return;
    identifyUser(user.id);
    // Disposable QA / internal identities are flagged so their traffic can be
    // excluded from production analytics. The email itself is never sent.
    markTestAccount(isTestAccountEmail(user.email));
  }, [user?.id, user?.email]);



  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });

    // With email confirmation enabled, signUp returns no session and the user
    // must click the emailed link. With auto-confirm on (current beta setup)
    // a session is returned immediately and no email is ever sent — the UI
    // must not promise one.
    return {
      error: error as Error | null,
      needsEmailConfirmation: !error && !data?.session,
    };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Reset analytics identity FIRST so a shared device can never inherit the
    // previous user's PostHog person, replay or flags.
    resetIdentity();
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("[useAuth] signOut error:", err);
    }
    // Clear local state regardless — if the server call failed (offline,
    // token already gone), we still want the UI to reflect a signed-out state.
    lastUserIdRef.current = null;
    setSession(null);
    setUser(null);
  };

  const requestPasswordReset = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    return { error: error as Error | null };
  };

  const reauthenticate = async (password: string) => {
    const email = user?.email ?? session?.user?.email;

    if (!email) {
      return { error: new Error("No authenticated user email found.") };
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const updateEmail = async (email: string) => {
    const { error } = await supabase.auth.updateUser({ email });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  const refreshUser = async () => {
    const { data } = await supabase.auth.getUser();
    setUser(data.user ?? null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        requestPasswordReset,
        reauthenticate,
        updateEmail,
        updatePassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
